export const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_KEY;
export const CMS_ENVIRONMENT = process.env.REACT_APP_CMS_ENVIRONMENT;
export const CATEGORIES: Record<string, string> = {
    // fi
    senioritabletti: "tablet",
    senioripuhelin: "phone",
    turvaranneke: "alarm",
    videopuheluappi: "app",
    videopuhelu_appi: "app",
    lisatarvikkeet: "accessories",
    // en
    "senior-tablet": "tablet",
    "senior-phone": "phone",
    "safety-bracelet": "alarm",
    "video-call-app": "app",
    accessories: "accessories",
    // sv
    seniorsurfplatta: "tablet",
    seniortelefon: "phone",
    trygghetslarm: "alarm",
    "videosamtals-app": "app",
    tillbehor: "accessories",
    // strapi locale slugs
    "en-tablet": "tablet",
    "en-phone": "phone",
    "en-alarm": "alarm",
    "en-app": "app",
    "en-accessories": "accessories",
    "en-ie-tablet": "tablet",
    "en-ie-phone": "phone",
    "en-ie-alarm": "alarm",
    "en-ie-app": "app",
    "en-ie-accessories": "accessories",
    "sv-tablet": "tablet",
    "sv-phone": "phone",
    "sv-alarm": "alarm",
    "sv-app": "app",
    "sv-accessories": "accessories",
    "sv-fi-tablet": "tablet",
    "sv-fi-phone": "phone",
    "sv-fi-alarm": "alarm",
    "sv-fi-app": "app",
    "sv-fi-accessories": "accessories",
};

export const ROLE = {
    NURSE: "nurse",
    HEAD_NURSE: "head_nurse",
    IT_SUPPORT: "it_support",
    CUSTOMER: "Customer",
    SENIOR: "senior",
    CUSTOMER_SMALL: "customer",
    RELATIVE: "relative",
};

export const categoryOptions = {
    tablet: "tablet",
    phone: "phone",
    app: "app",
    alarm: "alarm",
    accessories: "accessories",
};

export const USER_TYPE = {
    normal: "normal",
    appOnly: "apponly",
};

export const SHIPPING_METHODS = {
    direct: "direct",
    posti: "posti",
    posti_gadgets: "posti-gadgets",
    office_pickup: "office-pickup",
} as const;

export const REGIONS = [
    { code: "FI", countryCode: "FI", countryLabel: "Finland", label: "Finnish",  currency: "EUR", flag: "🇫🇮", lang: "fi", cmsLocale: "fi-FI", slugPrefix: "" },
    { code: "FI-SV", countryCode: "FI", countryLabel: "Finland", label: "Swedish", currency: "EUR", flag: "🇫🇮", lang: "sv", cmsLocale: "sv-FI", slugPrefix: "sv-fi" },
    { code: "SE", countryCode: "SE", countryLabel: "Sweden", label: "Swedish",  currency: "SEK", flag: "🇸🇪", lang: "sv", cmsLocale: "sv-SE", slugPrefix: "sv" },
    { code: "UK", countryCode: "UK", countryLabel: "United Kingdom", label: "English",  currency: "GBP", flag: "🇬🇧", lang: "en", cmsLocale: "en", slugPrefix: "en" },
    { code: "IE", countryCode: "IE", countryLabel: "Ireland", label: "English",  currency: "EUR", flag: "🇮🇪", lang: "en", cmsLocale: "en-IE", slugPrefix: "en-ie" },
    { code: "EN-GLOBAL", countryCode: "EN", countryLabel: "Global", label: "English",  currency: "EUR", flag: "🌍", lang: "en", cmsLocale: "en-IE", slugPrefix: "en-ie" },
] as const;

export type RegionCode = "FI" | "FI-SV" | "SE" | "UK" | "IE" | "EN-GLOBAL";
export type PricingRegionCode = "FI" | "SE" | "UK" | "IE" | "EN";
export const DEFAULT_REGION: RegionCode = "FI";

export const getRegionConfig = (regionCode?: string) => REGIONS.find((r) => r.code === regionCode) ?? REGIONS.find((r) => r.code === DEFAULT_REGION)!;

export const getPricingRegion = (regionCode?: string): PricingRegionCode => getRegionConfig(regionCode).countryCode as PricingRegionCode;
export const getSectionPriceRegion = (regionCode?: string): string => {
    if (regionCode === "EN" || regionCode === "EN-GLOBAL") return "EN";
    return getPricingRegion(regionCode);
};

export const getRegionLanguage = (regionCode?: string): "fi" | "sv" | "en" => getRegionConfig(regionCode).lang;

export const getRegionCmsLocale = (regionCode?: string): string => getRegionConfig(regionCode).cmsLocale;

export const getRegionSlugPrefix = (regionCode?: string): string => getRegionConfig(regionCode).slugPrefix;

const PRODUCT_REGION_EQUIVALENTS: Record<string, RegionCode[]> = {
    EN: ["EN-GLOBAL"],
    FI: ["FI"],
    SE: ["SE"],
    UK: ["UK"],
    IE: ["IE"],
};

export const normalizeProductRegions = (regions: string[] = []): RegionCode[] =>
    regions
        .flatMap((region) => PRODUCT_REGION_EQUIVALENTS[region] ?? [])
        .filter((region, index, list): region is RegionCode => list.indexOf(region) === index);

export const formatRegionLocale = (region: (typeof REGIONS)[number]): string => region.lang.toUpperCase();

export const formatRegionOption = (region: (typeof REGIONS)[number]): string => `${region.flag} ${formatRegionLocale(region)} · ${region.currency}`;

export const formatRegionDetails = (region: (typeof REGIONS)[number]): string => `${region.countryLabel} · ${region.label}`;

export const getPreferredLocaleFromRegion = (regionCode?: string): string => {
    if (regionCode === "EN-GLOBAL") return "en-Global";
    return `${getRegionLanguage(regionCode)}-${getPricingRegion(regionCode)}`;
};

export const getRegionCodeFromPreferredLocale = (preferredLocale?: string | null): RegionCode | null => {
    if (!preferredLocale) return null;
    const normalized = preferredLocale.toLowerCase();
    if (normalized === "en-global") return "EN-GLOBAL";
    if (normalized === "fi-fi") return "FI";
    if (normalized === "sv-fi") return "FI-SV";
    if (normalized === "sv-se") return "SE";
    if (normalized === "en-uk" || normalized === "en-gb") return "UK";
    if (normalized === "en") return "EN-GLOBAL";
    if (normalized === "en-ie") return "IE";
    return null;
};
